import {
  CHANNELS,
  UKRAINIAN_CITIES,
  DEVICES,
  PLATFORMS,
  CATEGORIES,
  UKRAINIAN_REGIONS,
  ALLOWED_COUNTRIES,
} from "@adlywise/shared-data";
import { useApp } from "@/contexts/AppContext";

export const SEGMENT_FIELDS = [
  { value: "name", label: "Profile Name", type: "string", deprecated: true },
  { value: "country", label: "Country", type: "string" },
  { value: "region", label: "Region", type: "string" },
  { value: "city", label: "City", type: "string" },
  { value: "channelId", label: "Channel ID", type: "number" },
  { value: "platform", label: "Platform", type: "string" },
  { value: "deviceType", label: "Device Type", type: "number" },
  { value: "categories", label: "Content Categories", type: "array" },
  { value: "tags", label: "Device Tags", type: "array" },
  { value: "createdAt", label: "Watch Time", type: "datetime" },
  { value: "adFrequency", label: "Ad Frequency", type: "number" },
  { value: "sessionDuration", label: "Session Duration (s)", type: "number" },
  { value: "providerUuid", label: "Provider / Publisher", type: "string" },
];

export const STATISTICS_FIELDS = [
  { value: "country", label: "Country", type: "string" },
  { value: "region", label: "Region", type: "string" },
  { value: "city", label: "City", type: "string" },
  { value: "channelId", label: "Channel", type: "number" },
  { value: "platform", label: "Platform", type: "string" },
  { value: "deviceType", label: "Device Type", type: "number" },
  { value: "categories", label: "Content Categories", type: "array" },
  { value: "createdAt", label: "Watch Time", type: "datetime" },
];

const BASE_OPERATORS = [
  { value: "eq", label: "Equals", types: ["string", "number", "boolean"] },
  {
    value: "neq",
    label: "Does Not Equal",
    types: ["string", "number", "boolean"],
  },
  {
    value: "in",
    label: "In List",
    types: ["string", "number"],
    valueType: "multi",
  },
  {
    value: "nin",
    label: "Not In List",
    types: ["string", "number"],
    valueType: "multi",
  },
  { value: "contains", label: "Contains", types: ["string"] },
  { value: "startsWith", label: "Starts With", types: ["string"] },
  { value: "endsWith", label: "Ends With", types: ["string"] },
  { value: "regex", label: "Matches Regex", types: ["string"] },
  {
    value: "arrayContains",
    label: "Array Contains All",
    types: ["array"],
    valueType: "multi",
  },
  {
    value: "intersects",
    label: "Array Intersects",
    types: ["array"],
    valueType: "multi",
  },
  { value: "gt", label: "Greater Than", types: ["number"] },
  { value: "gte", label: "Greater or Equal", types: ["number"] },
  { value: "lt", label: "Less Than", types: ["number"] },
  { value: "lte", label: "Less or Equal", types: ["number"] },
  { value: "between", label: "Between", types: ["number"], valueType: "range" },
  {
    value: "timeWindow",
    label: "Within Schedule",
    types: ["datetime"],
    valueType: "timeWindow",
    unique: true,
  },
];

export const getOperatorsForField = (fieldName) => {
  let field = STATISTICS_FIELDS.find((f) => f.value === fieldName);
  if (!field) {
    field = SEGMENT_FIELDS.find((f) => f.value === fieldName);
  }
  if (!field) return [];

  const stringRestrictedFields = [
    "deviceType",
    "platform",
    "channelId",
    "gender",
    "city",
    "region",
    "country",
  ];
  if (stringRestrictedFields.includes(field.value)) {
    return BASE_OPERATORS.filter((op) =>
      ["eq", "neq", "in", "nin"].includes(op.value),
    );
  }

  if (field.value === "providerUuid") {
    return BASE_OPERATORS.filter((op) => ["in", "nin"].includes(op.value));
  }

  return BASE_OPERATORS.filter((op) => op.types.includes(field.type));
};

export const getValueEditorType = (operator) => {
  if (!operator) return "text";
  if (operator.valueType === "multi") return "multi";
  if (operator.valueType === "range") return "range";
  if (operator.valueType === "timeWindow") return "timeWindow";
  if (operator.valueType === "none") return "none";
  return "text";
};

export const DEFAULT_TIME_WINDOW = {
  tz: "Europe/Kyiv",
  rules: [
    {
      days: ["mon", "tue", "wed", "thu", "fri"],
      start: "09:00",
      end: "21:00",
    },
  ],
};

export const DAYS_OF_WEEK = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

const makeOption = (value, label) => ({ value, label });

const cityOptions = Array.from(UKRAINIAN_CITIES.keys())
  .sort()
  .map((name) => makeOption(name, name));

const regionOptions = Array.from(UKRAINIAN_REGIONS.keys())
  .sort()
  .map((name) => makeOption(name, name));

const countryOptions = Array.from(ALLOWED_COUNTRIES.entries())
  .map(([code, name]) => makeOption(code, name))
  .sort((a, b) => {
    return a.label.localeCompare(b.label);
  });

const deviceOptions = Array.from(DEVICES.entries())
  .map(([name, id]) => ({ value: String(id), label: `${name} (ID ${id})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

const platformOptions = Array.from(PLATFORMS.keys())
  .sort()
  .map((name) => makeOption(name, name));

const baseChannelOptions = Array.from(CHANNELS.entries())
  .map(([id, info]) => ({
    value: String(id),
    label: `${info.name} • paid`,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const baseCategoryOptions = Array.from(CATEGORIES.entries())
  .map(([name, id]) => ({ value: id, label: `${name} (${id})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Channels/categories/tags arrive from /available-data already ordered by
// occurrence count (most-populous first). Preserve that server order rather than
// re-sorting alphabetically, so the busiest options surface at the top.
const createChannelOptions = (availableChannels) => {
  if (!Array.isArray(availableChannels) || availableChannels.length === 0) {
    return [];
  }

  return availableChannels.map((ch) => ({
    value: String(ch.id),
    label: ch.name,
    kind: ch.kind,
  }));
};

const createCategoryOptions = (availableCategories) => {
  if (!Array.isArray(availableCategories) || availableCategories.length === 0) {
    return [];
  }

  return availableCategories.map((cat) => ({
    value: String(cat.id),
    label: `${cat.name} (${cat.id})`,
  }));
};

const createTagOptions = (availableTags) => {
  if (!Array.isArray(availableTags) || availableTags.length === 0) {
    return [];
  }

  return availableTags
    .map((tag) => String(tag))
    .filter((tag) => tag.length > 0)
    .map((tag) => makeOption(tag, tag));
};

// Order a static curated option list by backend occurrence counts (desc), with a
// stable alphabetical tiebreak. Options with no counted data keep their relative
// order at the bottom. `counts` is a { [optionValue]: number } map.
const orderByCount = (options, counts) => {
  const map = counts || {};
  return [...options].sort((a, b) => {
    const diff = (map[b.value] ?? 0) - (map[a.value] ?? 0);
    if (diff !== 0) return diff;
    return a.label.localeCompare(b.label);
  });
};

const numberToString = (value) =>
  value === undefined || value === null ? "" : String(value);

const BASE_FIELD_OPTION_CONFIG = {
  city: {
    type: "single",
    options: cityOptions,
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  region: {
    type: "single",
    options: regionOptions,
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  country: {
    type: "single",
    options: countryOptions,
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  gender: {
    type: "single",
    options: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
    ],
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  deviceType: {
    type: "single",
    options: deviceOptions,
    toRaw: (value) => Number(value),
    toString: numberToString,
  },
  platform: {
    type: "single",
    options: platformOptions,
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  channelId: {
    type: "multi",
    options: baseChannelOptions,
    toRaw: (value) => Number(value),
    toString: numberToString,
  },
  categories: {
    type: "multi",
    options: baseCategoryOptions,
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  providerUuid: {
    type: "multi",
    options: [],
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
  tags: {
    type: "multi",
    options: [],
    toRaw: (value) => value,
    toString: (value) => (value ? String(value) : ""),
  },
};

export const useFieldOptionConfig = (customProviderOptions) => {
  const {
    availableChannels,
    availableCategories,
    availableTags,
    availableCounts,
  } = useApp();

  const counts = availableCounts || {};
  const providerOptions = Array.isArray(customProviderOptions)
    ? customProviderOptions
    : [];

  return {
    // Static curated lists, reordered by occurrence count (most-populous first).
    city: {
      ...BASE_FIELD_OPTION_CONFIG.city,
      options: orderByCount(cityOptions, counts.city),
    },
    region: {
      ...BASE_FIELD_OPTION_CONFIG.region,
      options: orderByCount(regionOptions, counts.region),
    },
    country: {
      ...BASE_FIELD_OPTION_CONFIG.country,
      options: orderByCount(countryOptions, counts.country),
    },
    gender: BASE_FIELD_OPTION_CONFIG.gender,
    deviceType: BASE_FIELD_OPTION_CONFIG.deviceType,
    platform: {
      ...BASE_FIELD_OPTION_CONFIG.platform,
      options: orderByCount(platformOptions, counts.platform),
    },
    // Server-ordered lists (already sorted by count) — keep their order as-is.
    channelId: {
      ...BASE_FIELD_OPTION_CONFIG.channelId,
      options: createChannelOptions(availableChannels),
    },
    categories: {
      ...BASE_FIELD_OPTION_CONFIG.categories,
      options: createCategoryOptions(availableCategories),
    },
    tags: {
      ...BASE_FIELD_OPTION_CONFIG.tags,
      options: createTagOptions(availableTags),
    },
    providerUuid: {
      ...BASE_FIELD_OPTION_CONFIG.providerUuid,
      options: orderByCount(providerOptions, counts.provider),
    },
  };
};

export const FIELD_OPTION_CONFIG = BASE_FIELD_OPTION_CONFIG;

export const getFieldOptionConfig = (field, customConfig) => {
  const config = customConfig || FIELD_OPTION_CONFIG;
  return config[field];
};
